
import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import slugify from "slugify";
import uniqueSlug from "unique-slug";

import GET_PROJECT_SKILLS from "@/apollo/skills/GetProjectSkills.gql";
import GET_PROJECT_TAGS from "@/apollo/Tags/GetProjectTags.gql";
import ManagersMultiselect from "@/components/autocompleteMultiselects/ManagersMultiselectNew.vue";
import InputFilter from "@/components/filters/InputFilter.vue";
import FormInputGroup from "@/components/form/FormInputGroupNew.vue";
import FormTextarea from "@/components/form/FormTextarea.vue";
import FormDatepicker from "@/components/form/FormDatepicker.vue";
import FormGroup from "@/components/form/FormGroupNew.vue";
import RouteNames from "@/entities/RouteNames";
import CREATE_PROJECT from "@/apollo/projects/CreateProject.gql";
import UPDATE_PROJECT from "@/apollo/projects/UpdateProject.gql";
import ProjectNameExists from "@/apollo/projects/ProjectNameExists.gql";
import ATTACH_TAGS from "@/apollo/autocomplete-queries/InsertProjectTag.gql";
import UIButton from "@/components/button/ui-button.vue";
import DeleteProjectTags from "@/apollo/Tags/DeleteProjectTags.gql";
import ModalFullScreen from "@/components/modal/ModalFullScreenNew.vue";
import ModalSectionsDivider from "@/components/modal/ModalSectionsDivider.vue";
import ProfileCountrySelect from "@/components/autocompleteMultiselects/ProfileCountrySelectNew.vue";

import {
  Project_Insert_Input,
  Project_Set_Input,
  Project_Skill,
  User,
} from "@/generated/graphql";
import { NOTIFICATION } from "@/entities/Events";
import Message from "@/data/notificationsMessages";
import Roles from "@/entities/Roles";
import LoadingOverlay from "@/components/common/LoadingOverlay.vue";
import CustomSkillsMultiselect from "@/components/autocompleteMultiselects/customOptions/CustomSkillsMultiselectNew.vue";
import CustomTagsMultiselect from "@/components/autocompleteMultiselects/customOptions/CustomTagsMultiselect.vue";
import SkillMultiselectItem from "@/components/skills/SkillMultiselectItem.vue";
import SkillMultiselectOption from "@/entities/SkillMultiselectOption";
import TagMultiselectOption from "@/entities/TagMultiselectOption";
import FormError from "@/components/common/FormError.vue";
import DropdownOption from "@/entities/DropdownOption";
import RegionInput from "@/components/autocompleteMultiselects/RegionInputNew.vue";
import ProjectDataForModal from "@/entities/modals/projectDataForModalNew";
import LocationModal from "@/views/clients/LocationModal.vue";
import { makeid } from "@/utils";

export interface ProjectForModal {
  name: string | null | undefined;
  country?: DropdownOption[];
  state?: DropdownOption[];
  city?: string | null;
  start_date: string | Date;
  end_date: string | Date;
  min_budget: number | null;
  max_budget: number | null;
  description: string;
  managersIds: string[];
  extra_notes: string;
  id?: null | string;
  company_id?: null | string;
}

export interface ProjectForDatabase {
  name: string;
  country: string | null;
  state?: string | null;
  city?: string | null;
  start_date: string | Date;
  end_date: string;
  min_budget: number | null;
  max_budget: number | null;
  description: string | null;
  id?: null | string;
  company_id: string;
  manager_id?: string;
  extra_notes: string | null;
  drafted: boolean;
  skillsIds?: number[];
  tagsIds?: number[];
}

interface UpdatePersonalSkills {
  id: number;
  user_id: null;
  company_id: string;
}

interface UpdateProjectArgs {
  set: Project_Set_Input;
  projectId: string;
  slug:string;
  addSkills: {
    project_id: string;
    skill_id: number;
  }[];
  updatePersonalSkills: UpdatePersonalSkills[];
  deleteSkillsIds: number[];
  customSkills: {
    name: string;
    company_id: string;
    project_skills: {
      data: {
        project_id: string;
      };
    };
  }[];
}

interface ProjectArgs {
  skillIds: number[];
  companyId: string;
  userId: string;
  updatedProject: Project_Insert_Input;
}

@Component({
  components: {
    RegionInput,
    FormError,
    SkillMultiselectItem,
    CustomSkillsMultiselect,
    LoadingOverlay,
    CustomTagsMultiselect,
    ManagersMultiselect,
    InputFilter,
    FormTextarea,
    FormGroup,
    FormDatepicker,
    FormInputGroup,
    ModalFullScreen,
    UIButton,
    ModalSectionsDivider,
    LocationModal,
    ProfileCountrySelect,
  },
  apollo: {
    skillsIds: {
      query: GET_PROJECT_SKILLS,
      variables() {
        return {
          projectId: this.data.id,
        };
      },
      update(data): void {
        const skillsIds = data.project_skill.map(
          ({ skill_id }: Project_Skill) => skill_id
        );
        this.projectSkillsIds = skillsIds;
        this.editedProjectSkillsIds = skillsIds;
      },
      skip(): boolean {
        return !Boolean(this.data?.id);
      },
    },
    tagsIds: {
      query: GET_PROJECT_TAGS,
      variables() {
        return {
          projectId: this.data.id,
        };
      },
      update(data): void {
        const tagsIds = data.project[0].project_tags.map(
          (tag_id) => tag_id
          );
        this.projectTagsIds = tagsIds.map(t => t.tag_id);
        sessionStorage.setItem("alltags", JSON.stringify(this.projectTagsIds));
        this.editedProjectTagsIds = tagsIds;
      },
      skip(): boolean {
        return !Boolean(this.data?.id);
      },
    },
  },
})
export default class ProjectModal extends Vue {
  @Prop({ type: Object, default: null })
  readonly data!: ProjectDataForModal | null;
  @Prop({ type: Boolean, default: false }) readonly dialog!: boolean;
  @Prop({ type: String, required: true }) readonly companyId!: string;
  @Prop({ type: Object, required: true }) readonly currentUser!: User;
  @Prop({ type: Boolean, default: false }) readonly isEdittingLocked!: boolean;
  @Prop({ type: Boolean, default: true })
  readonly show_cancel_draft_actions: boolean;

  valid: boolean = false;
  projectSkillsIds: number[] = [];
  editedProjectSkillsIds: number[] = [];
  projectTagsIds: number[] = [];
  editedProjectTagsIds: number[] = [];
  newSkillsNames: string[] = [];
  newTagsNames: string[] = [];
  personalSkills: SkillMultiselectOption[] = [];
  personalTags: TagMultiselectOption[] = [];
  projectTags: [];
  countryFromDB: string | null = "";
  projectSlug = "";
  projName: string = "";
  shouldAttachProject: boolean = false;

  project: ProjectForModal = {
    name: null,
    country: [],
    state: [],
    city: "",
    start_date: "",
    end_date: "",
    managersIds: [],
    min_budget: null,
    max_budget: null,
    extra_notes: "",
    description: "",
    id: null,
    company_id: null,
  };

  mounted() {
    if (this.data) {
      sessionStorage.setItem("allChoosentags", JSON.stringify(this.data.project_tags));
      this.projName = this.data.name;
      this.projectSlug = this.data?.slug;
      const managersIds = this.data.manager?.id ? [this.data.manager?.id] : [];
      const min_budget = this.data?.min_budget || null;
      const max_budget = this.data?.max_budget || null;
      const country = this.data.country;
      const state = this.data.state;
      this.countryFromDB = country || null;
      this.project = {
        name: this.data.name,
        country: country ? [{ id: country, name: country }] : [],
        state: state ? [{ id: state, name: state }] : [],
        city: this.data.city,
        start_date: this.data.start_date,
        end_date: this.data.end_date,
        managersIds,
        min_budget,
        max_budget,
        extra_notes: this.data.extra_notes || "",
        description: this.data.description || "",
        id: this.data.id,
      };
      this.$nextTick(() => {
       (this.$refs.minBudgetValidator as any).validate();
        (this.$refs.maxBudgetValidator as any).validate();
        (this.$refs.startDateValidator as any).validate();
        (this.$refs.endDateValidator as any).validate();
    });
    }
  }

  @Watch("countryName", { immediate: false })
  clearCountry(newCountry: string): void {
    if (newCountry !== this.countryFromDB || !Boolean(newCountry)) {
      this.project.state = [];
      this.project.city = "";
    }
  }

  validateMinBudget(value) {
    const maxBudget = this.project.max_budget;
    if (maxBudget !== null && value !== null && value > maxBudget) {
      return 'Min Budget must be less than or equal to Max Budget.';
    }
    return true;
  }

  validateMaxBudget(value) {
    const minBudget = this.project.min_budget;
    if (minBudget !== null && value !== null && value < minBudget) {
      return 'Max Budget must be greater than or equal to Min Budget.';
    }
    return true;
  }

  get countryName(): string {
    if (!this.project.country?.length) {
      return "";
    }
    return this.project.country[0].name;
  }

  get personalSkillsChosenWarning(): string {
    if (this.personalSkills.length === 0) {
      return "";
    }

    return this.personalSkills.map(({ name }) => name).join(", ");
  }

  get personalTagsChosenWarning(): string {
    if (this.personalTags.length === 0) {
      return "";
    }

    return this.personalTags.map(({ name }) => name).join(", ");
  }

  get modalTitle(): string {
    return `${this.modalPurpose} Project`;
  }

  get isManager(): boolean {
    return this.currentUser.role === Roles.MANAGER;
  }

  get projectId(): string | undefined {
    return this.data?.id;
  }

  get isCreatedProject(): boolean {
    return Boolean(this.data && !this.data.isNew);
  }
  get isActiveProject(): boolean {
    return Boolean(this.data && !this.data.drafted)
  }
  get modalPurpose(): string {
    return this.isCreatedProject ? "Edit" : "Create";
  }

  get buttonText(): string {
    return this.isActiveProject ? "Save" : "Publish";
  }

  get selectedManagersIds(): string[] {
    return this.project.managersIds;
  }

  get countryForDb(): string | null {
    if (!this.project.country?.length) {
      return null;
    }
    return this.project.country[0].name;
  }

  get stateForDb(): string | null {
    if (!this.project.state?.length) {
      return null;
    }
    return this.project.state[0].name;
  }

  get projectToSave(): ProjectForDatabase {
    return {
      name: this.project.name!,
      start_date: this.project.start_date,
      end_date: this.project.end_date as string,
      manager_id: this.currentUser?.manager
        ? this.currentUser.manager.id
        : this.project.managersIds[0],
      min_budget: this.project.min_budget || null,
      max_budget: this.project.max_budget || null,
      country: this.countryForDb,
      extra_notes: this.project.extra_notes,
      state: this.stateForDb,
      city: this.project.city || null,
      description: this.project.description,
      drafted: this.isCreatedProject,
      company_id: this.companyId,
      tagsIds: this.editedProjectTagsIds,
      skillsIds: this.editedProjectSkillsIds,
    };
  }

  isPersonalSkill(skill: SkillMultiselectOption): boolean {
    return skill.user_id === this.currentUser.id;
  }

  handleManagerInput(managersIds: string[]): void {
    this.project = {
      ...this.project,
      managersIds,
    };
  }

  updateSkills(
    skillsIds: number[],
    newSkillsNames: string[],
    allChosenSkills: SkillMultiselectOption[]
  ): void {
    this.editedProjectSkillsIds = skillsIds;
    this.newSkillsNames = newSkillsNames;

    this.personalSkills = allChosenSkills.filter((skill) =>
      Boolean(skill.user_id)
    );
  }

  updateTags(allChosenTags, newTagsNames: []): void {
    sessionStorage.setItem("alltags", JSON.stringify(allChosenTags));
    this.shouldAttachProject = newTagsNames.length > 0 ? true : false;
    this.editedProjectTagsIds = allChosenTags.map((tag) => tag.id);
    this.projectTags = allChosenTags;
    this.newTagsNames = newTagsNames;
  }

  handleSkillInput(skillsIds: number[]): void {
    this.editedProjectSkillsIds = skillsIds;
  }

  async onSubmit(isDrafted: false) {
    if (this.isCreatedProject) {

      this.handleUpdate(
        { ...this.projectToSave, id: this.project?.id },
        isDrafted
      );
    } else {
      await this.handleCreate(this.projectToSave, isDrafted);
    }
  }

  closeModal(): void {
    this.$root.$emit("projectsChanged");
    this.$emit("close");
  }

  // update and create
  async handleUpdate(
    project: ProjectForDatabase,
    isDrafted: boolean
  ): Promise<void> {
    try {
      const projectId = project.id!;
      const isnameExisit = await this.$apollo.mutate({
        mutation: ProjectNameExists,
        variables: {
          projectName: project.name,
        },
      });
      var isNameChanged = false;
      var shouldThrowError = true;
      const isnameExisitInDB = isnameExisit.data.project_aggregate.aggregate.count;
      if(this.projName.toLowerCase() === project.name.toLowerCase()){
        isNameChanged = true;
      }
      else{
         isNameChanged = false;
      }
      if (isNameChanged){
        shouldThrowError = true;
      }
      if(!isNameChanged && isnameExisitInDB >= 1){
        shouldThrowError = false;
      }
      if(shouldThrowError) {
        const updatedProject = await this._updateProject({
          set: {
            company_id: project.company_id,
            slug: this.projectSlug,
            name: project.name,
            start_date: project.start_date || null,
            drafted: isDrafted,
            end_date: project.end_date || null,
            manager_id: project.manager_id || null,
            min_budget: project.min_budget,
            max_budget: project.max_budget,
            country: this.countryForDb,
            state: this.stateForDb,
            city: project.city || null,
            extra_notes: project.extra_notes,
            description: project.description,
          },
          projectId,
          slug: this.projectSlug,
          deleteSkillsIds: this.projectSkillsIds,
          addSkills: this.editedProjectSkillsIds.map((skillId) => ({
            project_id: projectId,
            skill_id: skillId,
          })),
          updatePersonalSkills: this.personalSkills.map(({ id }) => ({
            id,
            user_id: null,
            company_id: this.companyId,
          })),
          customSkills: this.newSkillsNames.map((skillName) => ({
            name: skillName,
            company_id: this.companyId,
            project_skills: {
              data: {
                project_id: projectId,
              },
            },
          })),
        });
        await this.deleteProjectTags(projectId, this.editedProjectTagsIds);
        await this.handleTagsProject(projectId, this.editedProjectTagsIds);
        this.$emit("projectUpdated", updatedProject);
        this.$eventBus.emit(
          NOTIFICATION,
          Message.successMessage("Updated successfully")
        );
        this.closeModal();
      } 
      else{
        this.$eventBus.emit(
          NOTIFICATION,
          Message.errorMessage("Client name needs to be unique. please change the project title")
        );
      }
    } catch (e) {
      this.$eventBus.emit(NOTIFICATION, Message.errorMessage("Please try again!"));
    }
  }

  async _updateProject(updatedProject: UpdateProjectArgs) {
    return await this.$apollo.mutate({
      mutation: UPDATE_PROJECT,
      variables: updatedProject,
    });
  }

  async deleteProjectTags(projId,tagId){
    const tags = sessionStorage.getItem("allChoosentags");
    if (!tags) {
      return;
    }
    
    try {
      const parsedTags = JSON.parse(tags);
      if (parsedTags && parsedTags.length > 0) {
        const gettagIds = parsedTags.map((item) => (item.tag.id ? item.tag.id : item.tag));
        return await this.$apollo.mutate({
          mutation: DeleteProjectTags,
          variables: {
            projectId: projId ? projId : null,
            tagIds: gettagIds.map((tagId) => tagId),
          },
        });
      }
    } catch (error) {
      console.error("Error parsing allChoosentags from session storage:", error);
      sessionStorage.removeItem("allChoosentags");
    }
  }
  async handleTagsProject(projId, tagId) {
    const tags = sessionStorage.getItem("alltags");
    if(tags){
      const tagIds = JSON.parse(tags);
      const gettagIds = tagIds.map((item) => (item.id ? item.id : item));
      return await this.$apollo.mutate({
        mutation: ATTACH_TAGS,
        variables: {
          data: [...gettagIds.map((tagId) => ({
              project_id: projId ? projId : null,
              tag_id: tagId,
            })),
          ],
        },
      });
    }
  }
  async handleCreate(project: ProjectForDatabase, isDrafted): Promise<void> {
    try {
       const isnameExisit = await this.$apollo.mutate({
        mutation: ProjectNameExists,
        variables: {
          projectName: project.name,
        },
      });
      if (isnameExisit.data.project_aggregate.aggregate.count <= 0) {
      const response = await this._createProject({
        skillIds: this.editedProjectSkillsIds.map((skillId) => Number(skillId)),
        companyId: this.companyId,
        userId: this.currentUser.id,
        updatedProject: {
          company_id: project.company_id,
          slug: `${project.name}__${makeid(4)}`,
          name: project.name,
          start_date: project.start_date || null,
          end_date: project.end_date || null,
          manager_id: project.manager_id,
          min_budget: project.min_budget,
          max_budget: project.max_budget,
          country: project.country,
          state: project.state,
          city: project.city,
          description: project.description,
          extra_notes: project.extra_notes,
          drafted: isDrafted,
          project_skills: {
            data: [
              ...this.editedProjectSkillsIds.map((skillId) => ({
                skill_id: skillId,
              })),
              ...this.newSkillsNames.map((skillName) => ({
                skill: {
                  data: {
                    name: skillName,
                    company_id: this.companyId,
                  },
                },
              })),
            ],
          },
        },
      });
      const { insert_project_one } = response.data;
      await this.handleTagsProject(
        insert_project_one.id,
        this.editedProjectTagsIds
      );
      this.closeModal();
      this.$router.push({
        name: RouteNames.PROJECT_ACTIVITY,
        params: {
          id: insert_project_one.id,
        },
      });
      }
      else{
         this.$eventBus.emit(
          NOTIFICATION,
          Message.errorMessage("Client name needs to be unique. please change the project title")
        );

      }
    } catch (e) {
      this.$eventBus.emit(NOTIFICATION, Message.errorMessage("Please try again!"));
    }
  }

  async _createProject(variables: ProjectArgs) {
    return await this.$apollo.mutate({
      mutation: CREATE_PROJECT,
      variables,
    });
  }
}
